import { followupGoalName } from "@repo/shared";
import type {
  FollowupMode,
  FollowupSelectionContextResponse,
  FollowupSelectionRow,
} from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  createFollowupSelection,
  getFollowupSelectionContext,
  getMetadata,
} from "@/lib/api/flow-instances";
import { useFlows } from "@/lib/hooks/use-flows";
import { usePortfolioCollections } from "@/lib/hooks/use-portfolio-collections";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { ClientModeSummary } from "./client-mode-summary";
import { T } from "./followup-selection-dialog.i18n";
import { rowKey, SelectionChecklist, type SelectionKey } from "./selection-checklist";

// QL-150 phase 2 (§9.1, D4) — the ONE unified follow-up dialog. Three scope
// modes (locked 2026-09-04): a subset of accounts, a subset of savings
// agreements, or the entire client — never an arbitrary account+agreement
// mix. Client mode includes every eligible item (DP-2, exclusions rendered
// with their server reason) and seeds one successor goal per family of the
// selection context (a current goal or an unlinked item; a savings plan and
// its target accounts share one — QL-567; lineage grouping is QL-369).
// Entry points pre-select their mode and pre-tick their item; the
// client-level button opens client mode. Ineligible rows come from
// SERVER-provided per-item eligibility (D5); the mode rule itself is
// enforced server-side too.

/**
 * What opened the dialog — selects its mode and pre-ticks its item (D4).
 * QL-375 group 8 — a `mandate` target is an account follow-up of the
 * mandate's accounts: accounts mode, those accounts pre-ticked, and the flow
 * list limited to collections with mandates on (the mandate rides along only
 * there).
 */
export type FollowupTarget =
  | { kind: "account"; accountId: string; goalFlowInstanceId: string | null }
  | { kind: "agreement"; planId: string; goalFlowInstanceId: string | null }
  | {
      kind: "mandate";
      mandateId: string;
      /** The mandate's accounts that resolve to client accounts. */
      accountIds: string[];
      goalFlowInstanceId: string | null;
    }
  | { kind: "client" };

const modeForTarget = (target: FollowupTarget): FollowupMode =>
  target.kind === "account" || target.kind === "mandate"
    ? "accounts"
    : target.kind === "agreement"
      ? "agreements"
      : "client";

type Step =
  | { kind: "flows" }
  | { kind: "select"; flowId: string; context: FollowupSelectionContextResponse }
  | { kind: "blocked"; context: FollowupSelectionContextResponse };

type FollowupSelectionDialogProps = {
  clientId: string;
  /** null = dialog closed. */
  target: FollowupTarget | null;
  onClose: () => void;
  /** Alert-style error reporter (the section's useConfirm). */
  onError: (message: string) => void;
};

export function FollowupSelectionDialog({
  clientId,
  target,
  onClose,
  onError,
}: FollowupSelectionDialogProps) {
  const t = useT(T);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { flows } = useFlows();
  const { collections } = usePortfolioCollections();
  const [step, setStep] = useState<Step>({ kind: "flows" });
  const [mode, setMode] = useState<FollowupMode>("accounts");
  const [selected, setSelected] = useState<Set<SelectionKey>>(new Set());
  const [preselectedFlowId, setPreselectedFlowId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const open = target !== null;
  const busy = isLoading || isCreating;

  // Reset to the flow step ONLY when the dialog opens for a different target —
  // never on a background `flows` refetch (same guard as the old dialogs).
  const targetKey = target
    ? target.kind === "account"
      ? `account:${target.accountId}`
      : target.kind === "agreement"
        ? `agreement:${target.planId}`
        : target.kind === "mandate"
          ? `mandate:${target.mandateId}`
          : "client"
    : null;
  useEffect(() => {
    setStep({ kind: "flows" });
    setSelected(new Set());
  }, [targetKey]);

  // Pre-select the item's goal's flow when it is active.
  const flowInstanceId =
    target && target.kind !== "client" ? (target.goalFlowInstanceId ?? null) : null;
  useEffect(() => {
    setPreselectedFlowId(null);
    if (!flowInstanceId) return;
    let cancelled = false;
    getMetadata(flowInstanceId)
      .then((instance) => {
        if (cancelled || !instance?.flowId) return;
        // QL-401 — supported implies active (enable requires active;
        // deactivation forces the gate off), so it is the only check needed.
        const found = flows.find((f: any) => f.id === instance.flowId && f.supportedForFollowup);
        if (found) setPreselectedFlowId(instance.flowId);
      })
      .catch(() => {
        // Silently fail — no pre-selection, user picks manually
      });
    return () => {
      cancelled = true;
    };
  }, [flowInstanceId, flows]);

  const handleFlowSelect = async (flowId: string) => {
    if (!target) return;
    setIsLoading(true);
    try {
      const context = await getFollowupSelectionContext(clientId, flowId);
      if (!context.eligible) {
        setStep({ kind: "blocked", context });
        return;
      }
      // The entry point picks the mode and pre-ticks its item when selectable.
      setMode(modeForTarget(target));
      const preTicked = new Set<SelectionKey>();
      if (target.kind === "mandate") {
        // Every eligible account the mandate names; an ineligible one stays
        // unticked with its own reason, and the mandate rides on the rest.
        const wanted = new Set<SelectionKey>(
          target.accountIds.map((id) => `account:${id}` as const),
        );
        for (const row of context.families.flatMap((f) => f.items)) {
          if (row.eligible && wanted.has(rowKey(row))) preTicked.add(rowKey(row));
        }
      } else if (target.kind !== "client") {
        const wanted: SelectionKey =
          target.kind === "account" ? `account:${target.accountId}` : `agreement:${target.planId}`;
        const row = context.families.flatMap((f) => f.items).find((r) => rowKey(r) === wanted);
        if (row?.eligible) preTicked.add(wanted);
      }
      setSelected(preTicked);
      setStep({ kind: "select", flowId, context });
    } catch (err: any) {
      onError(err?.message || t.failedToCreateFollowup);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModeChange = (next: FollowupMode) => {
    setMode(next);
    // A mode is its own selection scope — ticks never carry across.
    setSelected(new Set());
  };

  const handleToggle = (key: SelectionKey, next: boolean) => {
    setSelected((prev) => {
      const nextSet = new Set(prev);
      if (next) nextSet.add(key);
      else nextSet.delete(key);
      return nextSet;
    });
  };

  const handleCreate = async (flowId: string) => {
    setIsCreating(true);
    try {
      const result = await createFollowupSelection(
        mode === "client"
          ? { flowId, clientId, mode }
          : {
              flowId,
              clientId,
              mode,
              selection: [...selected].map((key) => {
                const [type, ...rest] = key.split(":");
                return { type: type as "account" | "agreement", id: rest.join(":") };
              }),
            },
      );
      // The new instance (and its goals) appears in the client's lists — and
      // as a "Created" entry in the Activity tab (QL-275).
      queryClient.invalidateQueries({ queryKey: ["clients", clientId, "flowInstances"] });
      queryClient.invalidateQueries({ queryKey: ["clients", clientId, "goals"] });
      queryClient.invalidateQueries({ queryKey: ["clients", clientId, "activityEvents"] });
      onClose();
      navigate(`/clients/${clientId}/flows/${result.instanceId}`);
    } catch (err: any) {
      onError(err?.message || t.failedToCreateFollowup);
    } finally {
      setIsCreating(false);
    }
  };

  // QL-401 — the picker is a curated list of flows designated for followups.
  // `supportedForFollowup` implies active, so isActive is not re-checked.
  // QL-375 group 8 — from the Mandates tab only flows whose collection has
  // mandates on are offered: elsewhere the mandate would silently not ride along.
  const mandatesOnCollectionIds = new Set(
    collections.filter((c) => c.mandatesEnabled).map((c) => c.id),
  );
  const followupFlows = flows.filter(
    (f: any) =>
      f.supportedForFollowup &&
      (target?.kind !== "mandate" || mandatesOnCollectionIds.has(f.portfolioCollectionId)),
  );
  // The mandate line on account rows is shown only where it rides along.
  const showMandates = step.kind === "select" && step.context.mandatesEnabled;

  const allRows: FollowupSelectionRow[] =
    step.kind === "select" ? step.context.families.flatMap((f) => f.items) : [];
  const modeRows = allRows.filter(
    (r) => r.type === (mode === "agreements" ? "agreement" : "account"),
  );
  const clientEligibleCount = allRows.filter((r) => r.eligible).length;
  const selectionCount = mode === "client" ? clientEligibleCount : selected.size;

  // Per-family grouping (every mode): each ticked family seeds one successor
  // goal. A family is a current goal or an unlinked item — except that a
  // savings plan and its target accounts share one (QL-567). Preview the
  // resulting goals and gate on the flow's shape support.
  const familyByRowKey = new Map<SelectionKey, { index: number; name: string | null }>();
  // Unlinked rows of a multi-item family say where they land instead of
  // "No goal": the family's goal if it has one, else who they share the new
  // goal with.
  const familyHint = new Map<SelectionKey, string>();
  if (step.kind === "select") {
    step.context.families.forEach((f, index) => {
      for (const r of f.items) {
        familyByRowKey.set(rowKey(r), { index, name: f.name });
        if (r.goalName || f.items.length < 2) continue;
        familyHint.set(
          rowKey(r),
          f.goalIds.length > 0 && f.name
            ? t.joinsGoal(f.name)
            : t.sharesGoalWith(
                f.items
                  .filter((o) => o !== r)
                  .map((o) => o.name)
                  .join(", "),
              ),
        );
      }
    });
  }
  // Goal names come from the server's own naming rule (followupGoalName in
  // @repo/shared, called by createFollowup with the same inputs).
  const previewGoals: string[] = (() => {
    if (step.kind !== "select") return [];
    const families = step.context.families;
    const chosenByFamily = new Map<number, FollowupSelectionRow[]>();
    if (mode === "client") {
      families.forEach((f, index) => {
        const eligible = f.items.filter((r) => r.eligible);
        if (eligible.length > 0) chosenByFamily.set(index, eligible);
      });
    } else {
      for (const r of modeRows) {
        if (!selected.has(rowKey(r))) continue;
        const index = familyByRowKey.get(rowKey(r))?.index;
        if (index === undefined) continue;
        chosenByFamily.set(index, [...(chosenByFamily.get(index) ?? []), r]);
      }
    }
    return [...chosenByFamily.entries()].map(([index, chosen]) =>
      followupGoalName({
        // Every linked item of one family sits on the family's goal.
        sourceGoalName: chosen.find((r) => r.goalName)?.goalName ?? null,
        chosen,
        family: families[index]!.items,
      }),
    );
  })();

  // multiPortfolio gates participants per successor goal — i.e. per FAMILY in
  // every mode: two ticked items from different families are two one-portfolio
  // goals and need no multi-portfolio support.
  const multiBlocked =
    step.kind === "select" &&
    !step.context.multiPortfolio &&
    (mode === "client"
      ? step.context.families.some((f) => f.items.filter((r) => r.eligible).length > 1)
      : (() => {
          const counts = new Map<number, number>();
          for (const r of modeRows) {
            if (!selected.has(rowKey(r))) continue;
            const idx = familyByRowKey.get(rowKey(r))?.index ?? -1;
            counts.set(idx, (counts.get(idx) ?? 0) + 1);
          }
          return [...counts.values()].some((c) => c > 1);
        })());
  // A selection spanning several families seeds several goals — the flow must
  // be multi-goal (server twin: flow_not_multi_goal).
  const multiGoalBlocked =
    step.kind === "select" && !step.context.multiGoal && previewGoals.length > 1;
  // QL-593 — one successor goal per family; more families than the flow's cap
  // are refused (server twin: flow_max_goals_exceeded).
  const maxGoalsBlocked =
    step.kind === "select" && step.context.multiGoal && previewGoals.length > step.context.maxGoals;

  // QL-375 group 8 / Notion row 18 — a carried mandate on an UNLINKED account
  // lands on a fresh goal whose type the advisor sets; the type decides
  // whether the mandate is checked at all. Say so once, when it applies.
  const mandateGoalTypeNote =
    showMandates &&
    step.kind === "select" &&
    step.context.families.some(
      (f) =>
        f.goalIds.length === 0 &&
        f.items.some(
          (r) =>
            r.type === "account" &&
            r.eligible &&
            !!r.mandateId &&
            (mode === "client" || selected.has(rowKey(r))),
        ),
    );

  // Entire-client first; a subset mode is offered only when the client has
  // items of that type at all (the context lists every item, eligible or not).
  const hasAgreementRows = allRows.some((r) => r.type === "agreement");
  const hasAccountRows = allRows.some((r) => r.type === "account");
  // The single source for the mode tabs AND the header description below the
  // title — the description is looked up from here so the two cannot disagree.
  const modeOptions: Array<{
    value: FollowupMode;
    tabLabel: string;
    description: string;
  }> = [
    {
      value: "client",
      tabLabel: t.modeClientShort,
      description: t.modeClientDescription,
    },
    ...(hasAgreementRows
      ? [
          {
            value: "agreements" as const,
            tabLabel: t.modeAgreementsShort,
            description: t.modeAgreementsDescription,
          },
        ]
      : []),
    ...(hasAccountRows
      ? [
          {
            value: "accounts" as const,
            tabLabel: t.modeAccountsShort,
            description: t.modeAccountsDescription,
          },
        ]
      : []),
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!busy && !nextOpen) onClose();
      }}
    >
      <DialogContent>
        {step.kind === "flows" && (
          <>
            <DialogHeader>
              <DialogTitle>{t.selectFlow}</DialogTitle>
              <DialogDescription>{t.selectFlowDescription}</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {followupFlows.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {t.noFollowupFlows}
                </p>
              ) : (
                followupFlows.map((flow: any) => {
                  const isPreselected = flow.id === preselectedFlowId;
                  return (
                    <button
                      type="button"
                      key={flow.id}
                      className={cn(
                        "w-full text-left p-4 border rounded-lg hover:bg-accent cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed",
                        isPreselected && "border-primary bg-accent",
                      )}
                      onClick={() => handleFlowSelect(flow.id)}
                      disabled={busy}
                    >
                      <p className="font-medium">{flow.name}</p>
                      {flow.description && (
                        <p className="text-sm text-muted-foreground mt-1">{flow.description}</p>
                      )}
                    </button>
                  );
                })
              )}
              {isLoading && (
                <p className="text-sm text-muted-foreground text-center py-2">{t.loadingContext}</p>
              )}
            </div>
          </>
        )}

        {step.kind === "select" && (
          <>
            <DialogHeader>
              <DialogTitle>{t.selectTitle}</DialogTitle>
              {/* Reserve three lines so the header keeps its height when the
                  mode descriptions wrap differently — the dialog must not
                  jump on tab switch. */}
              <DialogDescription className="min-h-[3lh]">
                {modeOptions.find((o) => o.value === mode)?.description ?? ""}
              </DialogDescription>
            </DialogHeader>
            <Tabs value={mode} onValueChange={(v) => handleModeChange(v as FollowupMode)}>
              <TabsList className="w-full">
                {modeOptions.map((option) => (
                  <TabsTrigger
                    key={option.value}
                    value={option.value}
                    disabled={busy}
                    className="flex-1"
                  >
                    {option.tabLabel}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            {/* Fixed height (not max-h): each mode lists a different number of
                rows and the dialog must not resize when tabs switch. */}
            <div className="space-y-3 h-[360px] overflow-y-auto">
              {mode === "client" ? (
                allRows.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">{t.noItems}</p>
                ) : (
                  <ClientModeSummary
                    families={step.context.families.filter((f) => f.items.length > 0)}
                    showMandates={showMandates}
                  />
                )
              ) : modeRows.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {mode === "accounts" ? t.noAccounts : t.noAgreements}
                </p>
              ) : (
                <SelectionChecklist
                  rows={modeRows}
                  selected={selected}
                  familyHint={familyHint}
                  onToggle={handleToggle}
                  disabled={busy}
                  showMandates={showMandates}
                />
              )}
            </div>
            {/* What the selection will create — one goal per family (Q4). */}
            {previewGoals.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {t.createsGoals(previewGoals.length)} {previewGoals.join(" · ")}
              </p>
            )}
            {mandateGoalTypeNote && (
              <p className="text-xs text-muted-foreground">{t.mandateGoalTypeNote}</p>
            )}
            {multiBlocked && <p className="text-sm text-destructive">{t.multiPortfolioHint}</p>}
            {multiGoalBlocked && <p className="text-sm text-destructive">{t.multiGoalHint}</p>}
            {maxGoalsBlocked && step.kind === "select" && (
              <p className="text-sm text-destructive">{t.maxGoalsHint(step.context.maxGoals)}</p>
            )}
            <DialogFooter>
              <Button variant="outline" disabled={busy} onClick={() => setStep({ kind: "flows" })}>
                {t.back}
              </Button>
              <Button
                disabled={
                  busy ||
                  selectionCount === 0 ||
                  multiBlocked ||
                  multiGoalBlocked ||
                  maxGoalsBlocked
                }
                loading={isCreating}
                onClick={() => handleCreate(step.flowId)}
              >
                {selectionCount > 0 ? t.createCount(selectionCount) : t.create}
              </Button>
            </DialogFooter>
          </>
        )}

        {step.kind === "blocked" && (
          <>
            <DialogHeader>
              <DialogTitle>{t.blockedTitle}</DialogTitle>
              <DialogDescription>
                {step.context.reason === "not_supported_for_followup"
                  ? t.blockedNotSupported
                  : t.blockedNoCollection}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep({ kind: "flows" })}>
                {t.back}
              </Button>
              <DialogCancel>{t.cancel}</DialogCancel>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
